import { Scene } from "phaser";
import i18next from "i18next";
import { COLORS } from "../ui/colors";
import { ModernButton } from "../ui/modern-button";

export class NoInternetScene extends Scene {
  private background!: Phaser.GameObjects.Image;
  private noWifiIconCircle!: Phaser.GameObjects.Graphics;
  private noWifiIcon!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private messageText!: Phaser.GameObjects.Text;
  private retryButton!: ModernButton;
  private retryAttemptText!: Phaser.GameObjects.Text;
  private retryTimer?: Phaser.Time.TimerEvent;
  private autoRetryAttempts: number = 0;
  private maxAutoRetryAttempts: number = 30; // 30 seconds with 1 second interval

  constructor() {
    super({ key: "NoInternetScene" });
  }

  preload() {
    // Load the no-wifi icon
    this.load.image("no-wifi-icon", "icons/no-wifi-icon.svg");
    // Load the background image
    this.load.image("start-bg", "sprites/start-bg.png");
  }

  create() {
    const { width, height } = this.cameras.main;

    // Add the background image and center it
    this.background = this.add.image(width / 2, height / 2, "start-bg");
    this.background.setOrigin(0.5);
    this.background.setDisplaySize(width, height);

    // Add title text
    this.titleText = this.add.text(
      width / 2,
      height / 2 - 80,
      i18next.t("scenes:noInternet.title"),
      {
        fontSize: "32px",
        color: COLORS.YELLOW,
        align: "center",
        fontFamily: "Anton",
        stroke: "#000000",
        strokeThickness: 4,
        shadow: {
          offsetX: 0,
          offsetY: 4,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      }
    );
    this.titleText.setOrigin(0.5, 0.5);

    // Add black circle background for no-wifi icon (30px radius = 60px diameter)
    this.noWifiIconCircle = this.make.graphics({ x: 0, y: 0 });
    this.noWifiIconCircle.fillStyle(0x000000, 1);
    this.noWifiIconCircle.fillCircleShape(
      new Phaser.Geom.Circle(width / 2, height / 2 - 20, 30)
    );
    this.add.existing(this.noWifiIconCircle);

    // Add no-wifi icon under the title (40px wide)
    try {
      this.noWifiIcon = this.add.image(
        width / 2,
        height / 2 - 20,
        "no-wifi-icon"
      );
      this.noWifiIcon.setDisplaySize(40, 40);
    } catch (e) {
      console.warn("Could not load no-wifi icon, displaying text instead");
    }

    // Add message text
    this.messageText = this.add.text(
      width / 2,
      height / 2 + 40,
      i18next.t("scenes:noInternet.message"),
      {
        fontSize: "16px",
        color: COLORS.WHITE,
        align: "center",
        fontFamily: "Anton",
      }
    );
    this.messageText.setOrigin(0.5, 0.5);

    // Create modern retry button
    this.retryButton = new ModernButton({
      scene: this,
      x: width / 2,
      y: height / 2 + 100,
      text: i18next.t("scenes:noInternet.retry"),
      style: "success",
      onClick: () => this.attemptReconnect(),
    });

    // Add retry attempt counter
    this.retryAttemptText = this.add.text(
      width / 2,
      height / 2 + 160,
      i18next.t("scenes:noInternet.autoRetrying", { remaining: this.maxAutoRetryAttempts }),
      {
        fontSize: "12px",
        color: COLORS.WHITE,
        align: "center",
        fontFamily: "Anton",
      }
    );
    this.retryAttemptText.setOrigin(0.5, 0.5);

    // Start auto-retry timer
    this.startAutoRetry();

    // Handle scene resize
    this.scale.on("resize", () => {
      const { width: newWidth, height: newHeight } = this.cameras.main;
      this.resizeScene(newWidth, newHeight);
    });
  }

  private resizeScene(width: number, height: number) {
    // Resize background
    if (this.background) {
      this.background.setDisplaySize(width, height);
    }

    // Update positions
    if (this.titleText) {
      this.titleText.setPosition(width / 2, height / 2 - 80);
    }
    if (this.noWifiIconCircle) {
      this.noWifiIconCircle.clear();
      this.noWifiIconCircle.fillStyle(0x000000, 1);
      this.noWifiIconCircle.fillCircleShape(
        new Phaser.Geom.Circle(width / 2, height / 2 - 20, 30)
      );
    }
    if (this.noWifiIcon) {
      this.noWifiIcon.setPosition(width / 2, height / 2 - 20);
    }
    if (this.messageText) {
      this.messageText.setPosition(width / 2, height / 2 + 40);
    }
    if (this.retryButton) {
      this.retryButton.setPosition(width / 2, height / 2 + 100);
    }
    if (this.retryAttemptText) {
      this.retryAttemptText.setPosition(width / 2, height / 2 + 160);
    }
  }

  private startAutoRetry() {
    this.autoRetryAttempts = 0;
    this.retryTimer = this.time.addEvent({
      delay: 1000,
      callback: () => {
        this.autoRetryAttempts++;
        const remaining = this.maxAutoRetryAttempts - this.autoRetryAttempts;

        if (remaining > 0) {
          this.retryAttemptText.setText(i18next.t("scenes:noInternet.autoRetrying", { remaining }));
          this.attemptReconnect();
        } else {
          this.retryAttemptText.setText(i18next.t("scenes:noInternet.manualRetryRequired"));
        }
      },
      loop: true,
    });
  }

  private async attemptReconnect() {
    const serverUrl =
      (import.meta.env as any).VITE_SERVER_URL ||
      "https://mobile-extraction-server-eu.onrender.com";
    try {
      await fetch(serverUrl, {
        method: "HEAD",
        mode: "no-cors",
      });
      console.info("Internet connection restored");
      this.scene.start("LoadingScene");
    } catch (error) {
      console.warn("Still no internet connection");
      // Continue retrying
    }
  }

  shutdown() {
    if (this.retryTimer) {
      this.retryTimer.destroy();
    }
  }

  stop() {
    if (this.retryTimer) {
      this.retryTimer.destroy();
    }
  }
}
